import React from 'react';
import { ExtendedBrand, getShopName, getShopAddress, getShopCity, getShopState, getShopPin } from '../BrandTypes';
import { Button } from '../../../components/ui/Button';
import { Edit } from 'lucide-react';

interface ShopKycTabProps {
  selectedBrand: ExtendedBrand;
  isShopKycEditing: boolean;
  shopKycData: {name: string; address: string; state: string; city: string; pin: string; kycGstNo?: string} | null;
  errors: Record<string, string>;
  handleShopKycChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  setIsShopKycEditing: React.Dispatch<React.SetStateAction<boolean>>;
  saveShopKyc: () => void;
  isUpdating: boolean;
  canManageBrands: boolean;
  handleEditShopKyc: () => void;
}

export const ShopKycTab: React.FC<ShopKycTabProps> = ({
  selectedBrand,
  isShopKycEditing,
  shopKycData,
  errors,
  handleShopKycChange,
  setIsShopKycEditing,
  saveShopKyc,
  isUpdating,
  canManageBrands,
  handleEditShopKyc
}) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-medium text-gray-700">Shop KYC Details</h3>
        {selectedBrand.kyc?.shop && !isShopKycEditing && canManageBrands && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleEditShopKyc}
            className="text-xs"
          >
            <Edit size={14} className="mr-1" /> Edit Shop KYC
          </Button>
        )}
      </div>
      
      <div className="px-6 py-4">
        {isShopKycEditing && shopKycData ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* GST Number */}
              <div className="md:col-span-2">
                <label htmlFor="kycGstNo" className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number
                </label>
                <input
                  id="kycGstNo"
                  name="kycGstNo"
                  type="text"
                  value={shopKycData.kycGstNo || ''}
                  onChange={handleShopKycChange}
                  className={`w-full px-3 py-2 border ${errors.kycGstNo ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter GST number"
                />
                {errors.kycGstNo && (
                  <p className="mt-1 text-xs text-red-500">{errors.kycGstNo}</p>
                )}
              </div>
              
              {/* Shop Name */}
              <div className="md:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Shop Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={shopKycData.name}
                  onChange={handleShopKycChange}
                  className={`w-full px-3 py-2 border ${errors.shopName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter shop name"
                />
                {errors.shopName && (
                  <p className="mt-1 text-xs text-red-500">{errors.shopName}</p>
                )}
              </div>
              
              {/* Address */}
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Shop Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={shopKycData.address}
                  onChange={handleShopKycChange}
                  className={`w-full px-3 py-2 border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter complete address"
                />
                {errors.address && (
                  <p className="mt-1 text-xs text-red-500">{errors.address}</p>
                )}
              </div>
              
              {/* City */}
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={shopKycData.city}
                  onChange={handleShopKycChange}
                  className={`w-full px-3 py-2 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter city"
                />
                {errors.city && (
                  <p className="mt-1 text-xs text-red-500">{errors.city}</p>
                )}
              </div>
              
              {/* State */}
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  id="state"
                  name="state"
                  value={shopKycData.state}
                  onChange={handleShopKycChange}
                  className={`w-full px-3 py-2 border ${errors.state ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="">Select state</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                  <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                  <option value="Assam">Assam</option>
                  <option value="Bihar">Bihar</option>
                  <option value="Chhattisgarh">Chhattisgarh</option>
                  <option value="Goa">Goa</option>
                  <option value="Gujarat">Gujarat</option>
                  <option value="Haryana">Haryana</option>
                  <option value="Himachal Pradesh">Himachal Pradesh</option>
                  <option value="Jharkhand">Jharkhand</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Kerala">Kerala</option>
                  <option value="Madhya Pradesh">Madhya Pradesh</option>
                  <option value="Maharashtra">Maharashtra</option>
                  <option value="Manipur">Manipur</option>
                  <option value="Meghalaya">Meghalaya</option>
                  <option value="Mizoram">Mizoram</option>
                  <option value="Nagaland">Nagaland</option>
                  <option value="Odisha">Odisha</option>
                  <option value="Punjab">Punjab</option>
                  <option value="Rajasthan">Rajasthan</option>
                  <option value="Sikkim">Sikkim</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Telangana">Telangana</option>
                  <option value="Tripura">Tripura</option>
                  <option value="Uttar Pradesh">Uttar Pradesh</option>
                  <option value="Uttarakhand">Uttarakhand</option>
                  <option value="West Bengal">West Bengal</option>
                  <option value="Delhi">Delhi</option>
                </select>
                {errors.state && (
                  <p className="mt-1 text-xs text-red-500">{errors.state}</p>
                )}
              </div>
              
              {/* PIN Code */}
              <div>
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1">
                  PIN Code <span className="text-red-500">*</span>
                </label>
                <input
                  id="pin"
                  name="pin"
                  type="text"
                  value={shopKycData.pin}
                  onChange={handleShopKycChange}
                  className={`w-full px-3 py-2 border ${errors.pin ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter PIN code"
                  maxLength={6}
                />
                {errors.pin && (
                  <p className="mt-1 text-xs text-red-500">{errors.pin}</p>
                )}
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsShopKycEditing(false)}
              >
                Cancel
              </Button>
              <Button
                isLoading={isUpdating}
                onClick={saveShopKyc}
              >
                {isUpdating ? 'Saving...' : 'Save Shop Details'}
              </Button>
            </div>
          </div>
        ) : selectedBrand.kyc?.shop ? (
          <div className="grid grid-cols-1 gap-6">
            {/* GST Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number
              </label>
              <div className="flex items-center">
                <p className="text-gray-900">{selectedBrand.kycGstNo || 'Not provided'}</p>
              </div>
            </div>
            
            {/* Shop Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shop Name
              </label>
              <div className="flex items-center">
                <p className="text-gray-900">{getShopName(selectedBrand)}</p>
              </div>
            </div>
            
            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shop Address
              </label>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-gray-800">
                  {getShopAddress(selectedBrand) || "No address provided"}
                  {getShopCity(selectedBrand) ? `, ${getShopCity(selectedBrand)}` : ''}
                  {getShopState(selectedBrand) ? `, ${getShopState(selectedBrand)}` : ''}
                  {getShopPin(selectedBrand) ? ` - ${getShopPin(selectedBrand)}` : ''}
                </p>
              </div>
            </div>
            
            {/* KYC Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                KYC Status
              </label>
              <div className="flex items-center">
                <span className="bg-green-200 text-green-900 px-2 py-1 rounded-full text-sm font-semibold border border-green-300">
                  Verified
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Shop KYC Not Completed</h3>
            <p className="text-gray-500 mb-4">No shop details have been provided yet.</p>
            
            {canManageBrands && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleEditShopKyc}
              >
                Add Shop Details
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 